import type { CSSProperties, ReactNode } from 'react'
import { motion, type HTMLMotionProps, type Transition, type Variants } from 'framer-motion'
import { twMerge } from 'tailwind-merge'
import { NOTCH_TOAST_RADIUS } from '../../components/mini/notch/notch-toast'

/** Every view the setup call opens into is one object at different heights, so it holds one width. */
export const SETUP_CALL_FACE_WIDTH = 400

/** The cutout's bottom corner under every view, the notch toast's 16. */
export const SETUP_CALL_CORNER_RADIUS = NOTCH_TOAST_RADIUS

export type SetupCallFieldTone = 'invite' | 'quiet' | 'success'

// Champagne gold rising from the foot over near-black, as Prism's onboarding-call prototype paints it
const FIELD_BACKGROUND: Record<SetupCallFieldTone, string> = {
  invite: [
    'radial-gradient(80% 70% at 50% 115%, oklch(0.6 0.11 92 / 0.85), oklch(0.42 0.08 92 / 0.45) 45%, transparent 75%)',
    'linear-gradient(to bottom, oklch(0.14 0.004 92) 0%, oklch(0.16 0.012 92) calc(100% - 120px), oklch(0.19 0.028 92) 100%)',
  ].join(', '),
  quiet: [
    'radial-gradient(70% 55% at 50% 115%, oklch(0.6 0.11 92 / 0.6), oklch(0.42 0.08 92 / 0.3) 45%, transparent 75%)',
    'linear-gradient(to bottom, oklch(0.14 0.004 92) 0%, oklch(0.16 0.012 92) calc(100% - 120px), oklch(0.185 0.024 92) 100%)',
  ].join(', '),
  success: 'linear-gradient(to bottom, oklch(0.14 0.004 150) 0%, oklch(0.16 0.012 150) 100%)',
}

const GRID =
  'repeating-linear-gradient(90deg, oklch(1 0 0 / 0.034) 0 0.5px, transparent 0.5px 24px), repeating-linear-gradient(180deg, oklch(1 0 0 / 0.034) 0 0.5px, transparent 0.5px 24px)'

const GRAIN_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="160" height="160"><filter id="n"><feTurbulence type="fractalNoise" baseFrequency=".85" numOctaves="2" stitchTiles="stitch"/></filter><rect width="160" height="160" filter="url(#n)"/></svg>'
const GRAIN = `url("data:image/svg+xml,${encodeURIComponent(GRAIN_SVG)}")`

// Fades in from under the ears, so the field reads as grown out of the hardware's black
const FIELD_MASK = 'linear-gradient(to bottom, transparent 24px, #000 55%)'

/** Marks the field's root, and with `-ground` its box, so the booked water can find where to spread. */
export const SETUP_CALL_FIELD_ATTRIBUTE = 'data-setup-call-field'

/** The ground under every view: gold for the invite, turned down where the person is working, green once booked. */
export function SetupCallField({ tone, earsHeight }: { tone: SetupCallFieldTone; earsHeight: number }) {
  // Laid out from the notch's top edge, behind the ears, so its light sits where the prototype's does
  const style: CSSProperties = { top: -earsHeight, maskImage: FIELD_MASK, WebkitMaskImage: FIELD_MASK }
  return (
    <span
      aria-hidden
      data-setup-call-field-ground=""
      className="pointer-events-none absolute inset-x-0 bottom-0 overflow-hidden"
      style={style}
    >
      <span className="absolute inset-0" style={{ background: FIELD_BACKGROUND[tone] }} />
      {tone !== 'success' && (
        <span
          className="absolute inset-0"
          style={{
            background: GRID,
            maskImage: 'linear-gradient(to top, #000, transparent 40%)',
            WebkitMaskImage: 'linear-gradient(to top, #000, transparent 40%)',
          }}
        />
      )}
      <span className="absolute inset-0 opacity-25 mix-blend-overlay" style={{ background: GRAIN }} />
    </span>
  )
}

// The notch's own spring: a slight overshoot, most of the way there by a third of its length
const NOTCH_SPRING: Transition = { type: 'spring', duration: 0.45, bounce: 0.28 }
const ROW_FADE: Transition = { duration: 0.27, ease: [0.22, 1, 0.36, 1] }

export const faceVariants: Variants = {
  hidden: { opacity: 0, scale: 0.94, filter: 'blur(8px)' },
  visible: {
    opacity: 1,
    scale: 1,
    filter: 'blur(0px)',
    transition: { ...NOTCH_SPRING, delayChildren: 0.06, staggerChildren: 0.0375 },
  },
}

export const rowVariants: Variants = {
  hidden: { opacity: 0, filter: 'blur(8px)', y: 6 },
  visible: { opacity: 1, filter: 'blur(0px)', y: 0, transition: ROW_FADE },
}

/** One row of a view, arriving a beat after the one above it. */
export function SetupCallRow({
  children,
  ...props
}: Omit<HTMLMotionProps<'div'>, 'variants'> & { children: ReactNode }) {
  return (
    <motion.div variants={rowVariants} {...props}>
      {children}
    </motion.div>
  )
}

export const titleClassName = 'text-lg font-semibold text-foreground'

/** The main action: the brand yellow lit from inside, as a made thing rather than a flat chip. */
export const tactileClassName = twMerge(
  'flex-1 shadow-[inset_0_0_0_1px_oklch(0.94_0.1866_107.5),inset_0_0_2px_oklch(1_0_0),inset_0_1px_80px_oklch(1_0_0/0.08),inset_0_-8px_20px_oklch(1_0_0/0.17)]',
  'hover:bg-brand hover:brightness-[1.08] data-[disabled]:bg-[oklch(0.261_0.0376_107.5)] data-[disabled]:shadow-none',
)

export const letGoClassName = 'flex-1 text-[oklch(1_0_0/0.681)] data-[disabled]:text-foreground-disabled'

/** Chosen, everywhere in the booking: the info wash with a 1px edge in the on colour, the words in the info ink. */
export const chosenClassName =
  'bg-[oklch(0.273_0.0719_255.5)] text-[oklch(0.908_0.0521_255.5)] shadow-[inset_0_0_0_1px_oklch(0.624_0.2056_255.5)]'
export const chosenSubtleTextClassName = 'text-[oklch(0.77_0.1404_255.5)]'
